// src/common/types/types.ts

import {
  AuthPrimary,
  AvailabilityStatus,
  BonusTransactionType,
  ClusterTypes,
  CommonStates,
  ComplexOrderStatus,
  DeliveryService,
  DeliveryStatus,
  DeliveryType,
  EntityType,
  FieldType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  RatingValue,
  Status,
  TaskStatus,
  TaskType,
  Unit,
  ValueType,
} from "./enums";
import { SyncActionType } from "../../store/slices/syncSlice";
import { IconType } from "react-icons";

export interface Item {
  id: string;
  name: string;
  description: string;
  weight: number;
  images: string[];
  category: string;
  price: number;
}

export interface CartState {
  items: CartItem[];
  total: number;
}

export interface ItemProps {
  id: string;
  name: string;
  description: string;
  weight: number;
  images: string[];
  category: string;
  price: number;
}

export interface UserInterface {
  [key: string]: number | undefined | string;
  tg_user_name?: string;
  tg_image?: string;
  id?: number;
  uuid?: string;
  phone_number?: string;
  contacts?: number;
}

export interface OrderItem {
  id?: number;
  itemId: number | string;
  name: string;
  price: number;
  quantity: number;
  total: number;
  unit?: Unit;
}

export interface OrderTransitionInterface {
  id: number;
  orderId: number;
  status: OrderStatus;
  executeAt: string;
  executed: boolean;
  createdById?: number;
}

export interface CdekOrderData {
  tariffCode: number;
  pickupPointCode?: string;
  tariffName?: string;
  periodMin?: number;
  periodMax?: number;
  deliveryDateMin?: string;
  deliveryDateMax?: string;
}

export interface OrderDelivery {
  id?: number;
  orderId?: number;
  clusterId?: number;
  service?: DeliveryService;
  deliveryOrderId?: string;
  deliveryOrderUuid?: string;
  status?: DeliveryStatus;
  tariffName?: string;
  periodMin?: number;
  periodMax?: number;
  deliveryDateMin?: string;
  deliveryDateMax?: string;
  pickupPointCode?: string;
  pickupPointUuid?: string;
  pickupPointName?: string;
  pickupPointAddress?: string;
  pickupPointLatitude?: number;
  pickupPointLongitude?: number;
  pickupPointWorkTime?: string;
  pickupPointPhone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskInterface {
  id: number;
  orderId: number;
  clusterId: number;
  executorId: number | null;
  status: TaskStatus;
  fee: number;
  type: TaskType;
  createdAt: string;
  updatedAt: string;
  executor?: {
    id: number;
    name: string;
    phone_number: string;
  } | null;
}

export interface OrderInterface {
  id?: number;
  status: OrderStatus;
  deliveryType: DeliveryType;
  fullAddressId?: number;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  isPaid: boolean;
  items?: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  createdAt?: string;
  updatedAt?: string;
  notes?: string;
  orderNumber: string;
  storeId?: number;
  fullAddress?: FullAddress;
  store?: StoreInterface;
  metadata?: Record<string, any>;
  promoCode?: string;
  promoId?: number;
  discountCode?: string;
  discountAmount?: number;
  bonusesSpent?: number;
  desiredTime?: string;
  phone_number?: string;
  paymentMethodId?: number;
  description?: string;
  transitions?: OrderTransitionInterface[];
  selectedGiftItemId?: number;
  deliveryService?: DeliveryService;
  cdekOrder?: CdekOrderData;
  delivery?: OrderDelivery[];
  tasks?: TaskInterface[];
}

export interface OrderResponse extends OrderInterface {
  paymentInfo?: {
    qrCodeUrl?: string;
    qrCodeImage?: string;
    paymentLink?: string;
  };
  paymentToken?: string;
  paymentId?: string;
  payment_data?: any;
  paymentUrl?: string;
  paymasterInvoiceId?: string;
  paymentFailed?: boolean;
  paymentError?: string;
}
export interface AuthState {
  token: string;
}

export interface ListParams {
  limit?: number;
  offset?: number;
  lte?: Date;
  gte?: Date;
  search?: string;
  sort?: string;
  order?: "DESC" | "ASC";
  cityId?: string;
  searchFields?: string[];
  status?: string;
  userId?: string;
  selected?: number;
  categoryId?: number; // добавляем
}

export interface SaleListResponse {
  data: SaleItemInterface[];
  total: number;
}

export interface ItemStop {
  storeId: number;
  isStopped: boolean;
}

export interface SaleItemInterface {
  [key: string]: any;
  id: number;
  name: string;
  description: string;
  files: string[];
  categoryId?: string;
  barCode?: string;
  isActive: boolean;
  useStock: boolean;
  attributes: ItemAttribute[];

  complexProduct?: boolean;

  groupId?: string;
  saleItemId?: string;
  minQuantity?: number;
  quantityStep?: number;

  item_stops?: ItemStop[];

  selected?: {
    id: number | string;
    userId: number;
    itemId: number;
    selectedAt: Date | string;
    isActive: boolean;
  }[];

  ratings?: {
    id: number | string;
    userId: number;
    itemId: number;
    rating: number;
    comment?: string;
    files?: string[];
    isActive: boolean;
    createdAt: Date | string;
    updatedAt: Date | string;
  }[];

  views?: ViewItemInterface[];

  totalFavorites: number;
  totalViews: number;
  averageRating: number;
  totalRatings: number;

  allRatings?: {
    id: number | string;
    userId: number;
    itemId: number;
    rating: number;
    comment?: string;
    files?: string[];
    isActive: boolean;
    createdAt: Date | string;
    updatedAt: Date | string;
    user?: {
      id: number;
      name?: string;
      avatar?: string;
    };
  }[];

  addons: Array<{ addon: SaleItemInterface }>;
  recommendations?: Array<{ recommended: SaleItemInterface }> | null;
  stock_stores?: StockStore[];
}

export interface ItemAttribute {
  id?: string | number;
  name: string;
  fieldType: FieldType;
  valueType: ValueType;
  options: string[];
  unit: Unit;
  sortOrder: number;
  isRequired: boolean;
  isVisible: boolean;
  value: string | string[] | boolean;
  categoryAttributeId?: string | number;
  externalId?: string;
}

export interface CategoryListResponse {
  data: CategoryInterface[];
  total: number;
}

export interface CategoryInterface {
  [key: string]: any;
  id?: number;
  name: string;
  description: string;
  attributes: CategoryAttribute[];
  files: string[];
  sortOrder: number;
  children?: CategoryInterface[];
}

export interface CategoryAttribute {
  id: string | number;
  name: string;
  fieldType: FieldType;
  valueType: ValueType;
  options: string[];
  unit: Unit;
  sortOrder: number;
  isRequired: boolean;
  isVisible: boolean;
  externalId?: string;
}

export interface StoreListResponse {
  data: StoreInterface[];
  total: number;
}

export interface StoreDeliveryZone {
  id?: number;
  storeId?: number;
  clusterId?: number;
  clusterDeliveryId: number;
  polygon: Array<{ lat: number | string; lng: number | string }>;
}

export interface StoreInterface {
  [key: string]: any;
  id: number;
  name: string;
  description: string;
  address: AddressInterface;
  city: CityInterface;
  files: string[];
  createdAt?: Date;
  updatedAt?: Date;
  isActive: boolean;
  temporarilyDisabled: boolean;
  schedule: DaySchedule[];
  deliveryRadius?: number | string | null;
  withDelivery?: boolean;
  deliveryZones?: StoreDeliveryZone[];
}

export interface ClusterInterface {
  [key: string]: any;
  id?: number;
  name: string;
  description: string;
  isActive: boolean;
  token: string;
  port: string;
  type: ClusterTypes | "";
  withDelivery: boolean;
  complexSales?: boolean;
  regularSales?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  temporarilyDisabled: boolean;
  schedule: DaySchedule[];
  authPrimary: AuthPrimary;
  collectEmail: boolean;
  forceStore?: boolean;
  payment?: ClusterPaymentSettings;
  onePrice?: boolean;
  cdek?: CdekSettings;
}

export interface CdekSettings {
  enabled: boolean;
  insurance: boolean;
  markup: number;
  pickup: boolean;
  courier: boolean;
}

export interface ClusterPaymentSettings {
  cashOnDelivery?: boolean;
  cardOnDelivery?: boolean;
  onlineOnDelivery?: boolean;
  cashOnPickup?: boolean;
  cardOnPickup?: boolean;
  onlineOnPickup?: boolean;
}

export interface DaySchedule {
  sequence: number;
  from: string;
  to: string;
  dayOff: boolean;
}

export interface AddressInterface {
  value: string;
  latitude: number | string | undefined;
  longitude: number | string | undefined;
  isDirty?: boolean;
  notValid?: boolean;
}

export interface CityInterface {
  id?: number;
  value: string;
  latitude: number | string | undefined;
  longitude: number | string | undefined;
  isDirty?: boolean;
  notValid?: boolean;
}

export interface PromoListResponse {
  data: PromoInterface[];
  total: number;
}

export interface PromoInterface {
  sortOrder: number;
  id: number;
  createdAt?: Date;
  updatedAt?: Date;
  name: string;
  description: string;
  files: string[];
  isActive: boolean;
  startDate: Date;
  endDate: Date;
  discount: number;
  useItems: boolean;
  itemsFor: number[];
  useCategories: boolean;
  categoriesFor: number[];
  useStores: boolean;
  storesFor: number[];
  useDiscountCode: boolean;
  discountCode: string;
  datesImportant: boolean;
  clusterId?: number;
  isVisible?: boolean;
  showText: boolean;
  forFirstPurchase: boolean;

  forPickup?: boolean;
  giftBySum?: boolean;
  minSum?: number;
  giftItemIds?: number[];
}

export interface PriceItemInterface {
  id?: number;
  saleItemId: number;
  storeId: number;
  price: number;
}

export interface PointItemInterface {
  id?: string | number;
  sequence?: number;
  city: CityInterface;
  address: AddressInterface;
  status?: Status;
}

export interface GroupSelector {
  name: string;
  fieldType: FieldType;
  valueType: ValueType;
  unit: Unit;
  values: (string | number | boolean)[];
}

export interface FullAddress {
  id?: number;
  name: string;
  description?: string;
  city: CityInterface;
  address: AddressInterface;
  userId?: number;
  isGeneral?: boolean;
  createdAt?: string;
  updatedAt?: string;
  entrance?: string;
  floor?: string;
  apartment?: string;
  intercom?: string;
}

export interface ValidationResultInterface {
  [key: string]: string | boolean;
  name: string;
  isDirty: boolean;
  isEmpty: boolean;
  notValidFormat: boolean;
  tooShort: boolean;
  tooLong: boolean;
  isEmptyError: boolean;
  notValid: boolean;
  errorMessage: string;
}

export interface ValidationRulesInterface {
  fieldName: string;
  minLength?: number;
  maxLength?: number;
  format?: RegExp | "";
  canBeEmpty?: boolean;
  isEmptyMessage: string;
  toShortMessage: string;
  tooLongMessage: string;
  formatMessage: string;
}

export interface AuthResponse {
  accessToken?: string;
  refreshToken?: string;
  dateNotValid?: boolean;
  registerAction?: boolean;
  message?: string;
  type?: string;
}

export enum LoginStage {
  "getCode",
  "checkCode",
  "done",
  "botLoginProcess",
  "botLoginSuccess",
  "botLoginError",
  "botLink",
}

export interface AccessPayload {
  id: number;
  userId: number;
  iat: number;
  exp: number;
  contacts: number;
  phone_number?: string;
  uuid?: string;
  email?: string;
  clusterId?: number;
  user_clusters?: number[];
}

export interface Cart {
  items: CartItem[];
  total: number;
  deliveryCost?: number;
  promoCode?: string;
  userId?: number;
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  groupId?: string;
  selectedOptions?: Record<string, any>;
  variantId?: string;
  categoryId?: number;
  minQuantity: number;
  quantityStep: number;
  isGift?: boolean;
  attributes?: ItemAttribute[];
  unit?: Unit;
}

export interface Rule {
  level: number | string;
  value: number | string;
  id?: number;
}

export interface CommonState {
  [CommonStates.LOADING]: boolean;
}
export interface CommonStatePayload {
  stateName: CommonStates;
  state: boolean;
}

export interface BaseItemInteraction {
  id: number | string;
  userId: number;
  itemId: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ItemView extends BaseItemInteraction {
  viewedAt: Date;
}

export interface CreateItemViewData {
  userId: number;
  itemId: number;
  viewedAt?: Date;
}

export interface BatchItemViewData {
  userId: number;
  views: {
    itemId: number;
    viewedAt?: Date;
  }[];
}

export interface ItemViewStats {
  itemId: number | string;
  totalViews: number;
  uniqueViewers: number;
  lastViewedAt: Date;
}

export interface UserViewHistoryItem {
  itemId: number;
  viewedAt: Date;
  item?: SaleItemInterface;
}

export interface ItemSelected extends BaseItemInteraction {
  selectedAt: Date;
  isActive: boolean;
}

export interface CreateItemSelectedData {
  userId: number;
  itemId: number;
  selectedAt?: Date;
  isActive?: boolean;
}

export interface FavoriteItemInterface {
  id: number | string;
  itemId: number;
  selectedAt: Date;
  item?: SaleItemInterface;
}

export interface FavoriteStats {
  totalFavorites: number;
  addedToday: number;
}

export interface ItemRating extends BaseItemInteraction {
  rating: RatingValue;
  comment?: string;
  files?: string[];
  isActive: boolean;
}

export interface CreateItemRatingData {
  userId: number;
  itemId: number;
  rating: RatingValue;
  comment?: string;
  files?: string[];
  isActive?: boolean;
}

export interface RateItemData {
  userId: number;
  itemId: number;
  rating: RatingValue;
  comment?: string;
  files?: string[];
}

export interface ItemRatingStats {
  averageRating: number | string;
  totalRatings: number;
  distribution: Record<RatingValue, number>;
  withComments: number;
  withFiles: number;
}

export interface UserRating {
  id: number | string;
  itemId: number;
  rating: RatingValue;
  comment?: string;
  files?: string[];
  createdAt: Date;
  updatedAt: Date;
  item?: SaleItemInterface;
}

export interface ItemRatingWithUser extends ItemRating {
  user?: UserInterface;
}

export interface SaleItemWithInteractions {
  id: number;
  name: string;
  description?: string;
  price: number;
  categoryId?: number;
  files?: string[];
  createdAt: Date;
  updatedAt: Date;

  selected?: ItemSelected[];
  ratings?: ItemRating[];
  views?: ItemView[];

  totalFavorites: number;
  totalViews: number;
  averageRating: number;
  totalRatings: number;

  allRatings?: ItemRatingWithUser[];
}

export interface GetItemsQueryParams {
  category?: number;
  limit?: number;
  offset?: number;
  search?: string;
  sortBy?: "createdAt" | "price" | "rating" | "popularity";
  sortOrder?: "ASC" | "DESC";
  minPrice?: number;
  maxPrice?: number;
}

export interface RateItemDto {
  rating: RatingValue;
  comment?: string;
  files?: string[];
}

export interface ItemsListResponse {
  items: SaleItemWithInteractions[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface InteractionResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface UserInteractionsResponse {
  favorites: FavoriteItemInterface[];
  ratings: UserRating[];
  viewHistory: UserViewHistoryItem[];
}

export interface SyncAction {
  type: SyncActionType;
  itemId: number | string;
  data?: {
    rating?: RatingValue;
    comment?: string;
    files?: string[];
  };
  timestamp: number;
  localId?: string;
}

export interface GroupedProduct {
  // Существующие поля
  id?: number | string;
  name: string;
  description: string;
  files: string[];
  categoryId?: string;
  isActive?: boolean;
  useStock?: boolean;
  attributes: ItemAttribute[];
  selectors: Record<string, any>;
  variants: SaleItemInterface[]; // Варианты тоже имеют взаимодействия
  type: string;
  groupId: string;
  category: CategoryInterface;
  currentVariant: SaleItemInterface;

  prices: PriceItemInterface[];

  // Новые поля (аналогично SaleItemInterface)
  selected?: {
    id: number | string;
    userId: number;
    itemId: number;
    selectedAt: Date;
    isActive: boolean;
  }[];

  ratings?: {
    id: number | string;
    userId: number;
    itemId: number;
    rating: number;
    comment?: string;
    files?: string[];
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date;
  }[];

  views?: ViewItemInterface[];

  totalFavorites?: number;
  totalViews?: number;
  averageRating?: number;
  totalRatings?: number;
  allRatings?: {
    id: number | string;
    userId: number;
    itemId: number;
    rating: number;
    comment?: string;
    files?: string[];
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date;
    user?: {
      id: number | string;
      name?: string;
      avatar?: string;
    };
  }[];
}

export interface ViewItemInterface {
  id: number | string;
  userId: number;
  itemId: number;
  viewedAt: Date | string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface Selector {
  name: string;
  unit: number;
  values: (string | number)[];
}

export interface SelectedItemInterface {
  id: string | number;
  userId: number;
  itemId: number;
  selectedAt: string;
  isActive: boolean;
}

export interface BonusBalanceResponse {
  balance: number;
  totalSpent: number;
  bonusPercent: number;
}

export interface BonusTransaction {
  id: number;
  amount: number;
  type: BonusTransactionType;
  description: string;
  orderId?: number;
  balanceSnapshot: number;
  createdAt: string;
}

export interface BonusStats {
  balance: number;
  totalEarned: number;
  totalSpent: number;
}

export interface StockStore {
  quantity: number;
  reserved: number;
  store?: {
    id: number;
    name: string;
  };
}

export interface PaymentInfo {
  qrCodeUrl?: string;
  qrCodeImage?: string;
  paymentLink?: string;
}

export interface DeliveryRule {
  id: number;
  level: number;
  value: number;
  radius: number;
}

export interface RegisterEmailPayload {
  email: string;
  code: string;
  clusterId?: string;
}

export interface LatLng {
  lat: number;
  lng: number;
}

export type ContentAlign = "left" | "center" | "right";

export interface PaymentMethodInterface {
  id: number;
  userId: number;
  paymentMethodId: string; // ID в ЮKassa
  last4: string; // последние 4 цифры
  cardType: string; // Visa, Mastercard и т.д.
  expiryMonth: string; // MM
  expiryYear: string; // YYYY
  isActive: boolean; // активна/удалена
  is_default: boolean; // основная ли карта
  createdAt?: string;
  updatedAt?: string;
}

export interface PaymentMethodsResponse {
  data: PaymentMethodInterface[];
  total: number;
}

// Для создания/обновления
export interface CreatePaymentMethodDto {
  paymentMethodId: string;
  last4: string;
  cardType: string;
  expiryMonth: string;
  expiryYear: string;
}

export interface PaymentMethodsResponse {
  data: PaymentMethodInterface[];
  total: number;
}

export interface CreatePaymentResponse {
  token: string;
  id: string;
}

export interface AvailabilityParams {
  item: SaleItemInterface;
  forceStore: boolean;
  selectedStoreIds: number[];
}

export interface AvailabilityResult {
  available: boolean;
  status: AvailabilityStatus;
  message?: string;
}

export type ButtonConfig = {
  path: string;
  icon: IconType;
  label: string;
  onClick?: () => void;
};

export interface ComplexOrderItem {
  id: string;
  itemId: number;
  name: string;
  price: number;
  quantity: number;
  total: number;
  image?: string;
  categoryId?: number;
  minQuantity: number;
  quantityStep: number;
  attributes?: Record<string, any>;
  parentId?: string;
  unit?: Unit;
}

export interface ComplexOrderState {
  items: ComplexOrderItem[];
  isOpen: boolean;
}

export interface CreateComplexOrderPayload {
  items: {
    itemId: number;
    name: string;
    quantity: number;
    basePrice: number;
    total: number;
  }[];
  status: number;
  total: number;
  phone_number: string;
  notes?: string;
  data?: {
    fieldTemplateId: number;
    type: string;
    value: string;
  }[];
}

export interface ComplexOrderItemInterface {
  id: number;
  itemId: number;
  name: string;
  quantity: number;
  basePrice: number;
  manualPrice: number | null;
  total: number;
  complexOrderId: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ComplexOrderDataInterface {
  id: number;
  complexOrderId: number;
  label: string | null;
  type: string; // 'phone', 'email', 'comment', 'custom'
  value: string;
  createdAt: Date;
  updatedAt: Date;
  fieldTemplateId: number;
}

export interface ComplexOrderManagerInterface {
  id: number;
  complexOrderId: number;
  managerId: number;
  role: string; // 'owner', 'assistant', 'observer'
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ComplexOrderInterface {
  id: number;
  status: ComplexOrderStatus;
  total: number;
  phone_number: string;
  customerName: string | null;
  desiredTime: Date | null;
  notes: string | null;
  clusterId: number;
  userId: number | null;
  fullAddressId: number | null;
  createdAt: Date;
  updatedAt: Date;
  items?: ComplexOrderItemInterface[];
  data?: ComplexOrderDataInterface[];
  managers?: ComplexOrderManagerInterface[];
}

export interface ServiceOtpResponse {
  codes: string[];
  codesLeft: number;
}

export interface ExecutorInterface {
  id?: number;
  name: string;
  phone_number: string;
  position?: string;
  isDriver: boolean;
  canServe: boolean;
  trackLocation: boolean;
  isActive: boolean;
  deleted: boolean;
  uuid?: string;
  clusterId: number;
  motivationId: number;
  userId?: number;
  publish: boolean;
  publishMotivation: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExecutorMeResponse extends ExecutorInterface {
  motivation?: MotivationInterface;
  stores?: StoreExecutorInterface[];
  isAdmin?: boolean;
}

export interface MotivationInterface {
  id?: number;
  name: string;
  description?: string;
  costPerOrder?: number;
  revenuePercent?: number;
  bonusForOrdersCount?: number;
  ordersCountTarget?: number;
  bonusForRevenue?: number;
  revenueTarget?: number;
  metadata?: Record<string, any>;
  deleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
  rules?: MotivationRuleInterface[];
}

export interface StoreExecutorInterface {
  storeId: number;
  executorId: number;
  store?: { id: number; name: string };
}

export interface MotivationRuleInterface {
  id?: number;
  motivationId: number;
  categoryIds?: number[];
  itemIds?: number[];
  costPerOrder?: number;
  revenuePercent?: number;
  deleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface LeadFormData {
  phone: string;
  consent?: boolean;
}

export interface ListByIdsPayload {
  ids: number[];
}

export interface CdekPackage {
  weight: number;
  length: number;
  width: number;
  height: number;
}

export interface CdekCalculateRequest {
  storeId: number;
  addressId: number;
  packages: CdekPackage[];
}

export interface CdekTariff {
  tariff_code: number;
  tariff_name: string;
  tariff_description: string;
  delivery_mode: number;
  delivery_sum: number;
  period_min: number;
  period_max: number;
  calendar_min: number;
  calendar_max: number;
  delivery_date_range: {
    min: string;
    max: string;
  };
}

export interface CdekPickupPoint {
  code: string;
  name: string;
  type: "PVZ" | "POSTAMAT";
  work_time: string;
  phones: Array<{ number: string }>;
  address?: string;
  location: {
    address: string;
    address_full: string;
    latitude: number;
    longitude: number;
  };
}

export interface CdekCalculateResponse {
  tariffs: CdekTariff[];
  pickupPoints: CdekPickupPoint[];
}

export interface DocumentLinkInterface {
  id?: number;
  documentId?: number;
  clusterId?: number;
  entityType: EntityType;
  entityId?: number | null;
  isAll: boolean;
  sortOrder: number;
}

export interface DocumentInterface {
  id?: number;
  name: string;
  description?: string;
  filePath?: string;
  mimeType?: string;
  size?: number;
  isPrivate: boolean;
  userId?: number;
  clusterId?: number;
  isActive?: boolean;
  deleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
  links: DocumentLinkInterface[];
}

export interface AboutSection {
  title: string
  text: string
  image?: string
}

export interface AboutContent {
  title: string
  metaDescription: string
  heroImage: string
  heroImageAlt?: string
  intro: string
  sections: AboutSection[]
}

export interface Content {
  about: AboutContent
}